import { isProvisional, standingClasses, standingTone } from '../api/attendance'
import type { ClassAttendance } from '../api/portal'

/**
 * "14 of 16", coloured.
 *
 * **One component for all three audiences.** Tonya on the dancer record, the
 * teacher on her roster and the parent in the portal read the same badge and
 * must read it as the same thing — three screens each picking their own bands
 * is how "but she looked fine on my page" starts (D-168, D-169).
 *
 * The counts lead and the percentage follows in brackets, because a fraction can
 * be argued with and a bare percentage cannot. When a parent asks how the studio
 * got 78%, both terms are already on screen.
 *
 * A figure computed over registers nobody has filled in renders as a dashed
 * outline rather than a solid fill (D-118) — still readable at a glance, but
 * visibly not final.
 */
export default function StandingBadge({
  attended,
  counted,
  percentage,
  notYetMarked,
  threshold,
  size = 'normal',
  gated = true,
}: {
  attended: number
  counted: number
  percentage: number | null
  notYetMarked: number
  /** From settings, never assumed — Tonya can move the 80% rule (D-168). */
  threshold: number
  size?: 'normal' | 'small'
  /**
   * False for a rec dancer, who is measured but never gated (PRD §7.6).
   *
   * The counts still show — the studio tracks them so a pattern can be noticed —
   * but neutral, because colouring them against a threshold would invent a rule
   * the studio does not have and put a red badge in front of a family that is
   * breaking none.
   */
  gated?: boolean
}) {
  const provisional = isProvisional(notYetMarked)
  const tone = gated === false ? 'mute' : standingTone(percentage, threshold)

  const shape =
    size === 'small'
      ? 'rounded-md px-1.5 py-0.5 text-[11.5px]'
      : 'rounded-lg px-2 py-1 text-[12.5px]'

  // Nothing countable yet reads as words, never as 0% — a class that has not met
  // must not make a dancer look like she never turns up.
  if (percentage === null) {
    return (
      <span className={`tabular inline-flex font-semibold ${shape} ${standingClasses('mute', false)}`}>
        No classes yet
      </span>
    )
  }

  return (
    <span
      className={`tabular inline-flex items-baseline gap-1 font-semibold ${shape} ${standingClasses(tone, provisional)}`}
      title={
        provisional
          ? `${notYetMarked} ${notYetMarked === 1 ? 'class has' : 'classes have'} not been marked yet, so this will change`
          : undefined
      }
    >
      {attended} of {counted}
      <span className="opacity-70">({percentage}%)</span>
    </span>
  )
}

// `OverallBadge` lived here until D-589: the whole-dancer figure is the
// shared panel's now, and the dancer record was its last reader.

/** One class's contribution to it. Same colours, no verdict. */
export function ClassBadge({
  attendance,
  threshold,
  size,
  gated = true,
}: {
  attendance: ClassAttendance
  threshold: number
  size?: 'normal' | 'small'
  gated?: boolean
}) {
  return (
    <StandingBadge
      gated={gated}
      attended={attendance.attended}
      counted={attendance.counted}
      percentage={attendance.percentage}
      notYetMarked={attendance.notYetMarked}
      threshold={threshold}
      size={size}
    />
  )
}
